import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { 
    Truck, Users, Shield, BarChart3, Camera, Wrench,
    Check, ChevronRight, Menu, X, Globe, Car, Package, User, Layers
, Handshake } from 'lucide-react';
import { WorkshopEditionCard } from '../components/landing/WorkshopEditionCard';

export default function LandingPage() {
    const navigate = useNavigate();
    const { language, toggleLanguage, t } = useLanguage();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [selectedEdition, setSelectedEdition] = useState('carrier'); // 'personal', 'inspection', 'carrier' or 'hybrid'

    // Edition-specific pricing
    const editionPricing = {
        personal: {
            basic: 2.5,
            professional: 2.5,
            enterprise: 2.5
        },
        inspection: {
            basic: 6,
            professional: 5,
            enterprise: 4
        },
        carrier: {
            basic: 9,
            professional: 7,
            enterprise: 5
        },
        leasing: {
            basic: 6,
            professional: 5,
            enterprise: 4
        },
        workshop: { basic: 0, professional: 0, enterprise: 0 },
        hybrid: {
            // Hybrid shows range: inspection price / carrier price
            basic: { min: 6, max: 9 },
            professional: { min: 5, max: 7 },
            enterprise: { min: 4, max: 5 }
        }
    };

    const currentPricing = editionPricing[selectedEdition];
    
    // Helper to format price display
    const formatPrice = (price) => {
        if (typeof price === 'object' && price.min !== undefined) {
            return `${price.min}/${price.max}`;
        }
        return price.toString();
    };
    
    // Helper to get numeric price for calculations
    const getNumericPrice = (price) => {
        if (typeof price === 'object' && price.max !== undefined) {
            return price.max; // Use max for display purposes
        }
        return price;
    };

    // Personal Edition has a single simplified plan - Family use
    const personalPlan = {
        name: language === 'el' ? 'Οικογενειακή Χρήση' : 'Family Use',
        description: language === 'el' ? 'Για οικογένειες με έως 3 οχήματα & 3 χρήστες' : 'For families with up to 3 vehicles & 3 users',
        vehicleLimit: 3,
        pricePerVehicle: 2.5,
        features: [
            language === 'el' ? '👨‍👩‍👧 Έως 3 χρήστες (οικογένεια)' : '👨‍👩‍👧 Up to 3 users (family)',
            language === 'el' ? '🚗 Έως 3 οχήματα' : '🚗 Up to 3 vehicles',
            language === 'el' ? '€2.50/όχημα/μήνα' : '€2.50/vehicle/month',
            language === 'el' ? '📱 Mobile-first εφαρμογή' : '📱 Mobile-first app',
            language === 'el' ? '🅿️ Χάρτης Στάθμευσης' : '🅿️ Parking Map',
            language === 'el' ? '📷 Parking Safety' : '📷 Parking Safety',
            language === 'el' ? '🔧 Συντήρηση & Υπενθυμίσεις' : '🔧 Maintenance & Reminders',
            language === 'el' ? '🤖 AI Ανίχνευση Ζημιών' : '🤖 AI Damage Detection',
            language === 'el' ? '📧 Email Υποστήριξη' : '📧 Email Support'
        ],
        highlighted: true,
        cta: language === 'el' ? 'Ξεκινήστε Δωρεάν' : 'Start Free Trial',
        badge: language === 'el' ? 'Για Οικογένειες' : 'For Families'
    };

    const leasingFeatures = (count, price) => [
        t('landing.upToVehicles').replace('{count}', count),
        t('landing.perVehicleMonth').replace('{price}', price),
        language === 'el' ? 'Αυτόματη σύνδεση με τους μισθωτές σας' : 'Automatic linking with your lessees',
        language === 'el' ? 'Ανάθεση οχημάτων με επιβεβαίωση πινακίδας' : 'Vehicle assignment with plate confirmation',
        language === 'el' ? 'Χιλιόμετρα & check-in/check-out ανά μισθωτή' : 'Km & check-in/check-out per lessee',
        language === 'el' ? 'Ειδοποιήσεις λήξης σύμβασης & ορίου km' : 'Contract end & km limit alerts',
        language === 'el' ? 'Ανανέωση σύμβασης με ένα κλικ' : 'One-click contract renewal',
        t('landing.emailSupport')
    ];

    const basePlans = selectedEdition === 'personal' ? [personalPlan] : [
        {
            name: t('landing.basic'),
            description: t('landing.basicDesc'),
            vehicleLimit: 7,
            pricePerVehicle: currentPricing.basic,
            features: (selectedEdition === 'carrier' || selectedEdition === 'hybrid') ? [
                t('landing.upToVehicles').replace('{count}', '7'),
                t('landing.unlimitedDrivers'),
                t('landing.perVehicleMonth').replace('{price}', formatPrice(currentPricing.basic)),
                t('landing.allFeatures'),
                t('landing.dailyCheckIns'),
                t('landing.maintenanceSchedulingFeature'),
                t('landing.photoComparison'),
                t('landing.aiDamageDetection'),
                language === 'el' ? 'AI Ανάλυση Φορτίου' : 'AI Cargo Analysis',
                t('landing.emailSupport')
            ] : [
                t('landing.upToVehicles').replace('{count}', '7'),
                t('landing.unlimitedDrivers'),
                t('landing.perVehicleMonth').replace('{price}', formatPrice(currentPricing.basic)),
                t('landing.dailyCheckIns'),
                t('landing.maintenanceSchedulingFeature'),
                t('landing.photoComparison'),
                t('landing.aiDamageDetection'),
                t('landing.emailSupport')
            ],
            highlighted: false,
            cta: t('landing.startFreeTrial')
        },
        {
            name: t('landing.professional'),
            description: t('landing.professionalDesc'),
            vehicleLimit: 15,
            pricePerVehicle: currentPricing.professional,
            features: (selectedEdition === 'carrier' || selectedEdition === 'hybrid') ? [
                t('landing.upToVehicles').replace('{count}', '15'),
                t('landing.unlimitedDrivers'),
                t('landing.perVehicleMonth').replace('{price}', formatPrice(currentPricing.professional)),
                t('landing.allFeatures'),
                t('landing.dailyCheckIns'),
                t('landing.maintenanceSchedulingFeature'),
                t('landing.aiDamageDetection'),
                language === 'el' ? 'AI Ανάλυση Φορτίου' : 'AI Cargo Analysis',
                t('landing.analyticsReportsFeature'),
                t('landing.csvExports'),
                t('landing.prioritySupport')
            ] : [
                t('landing.upToVehicles').replace('{count}', '15'),
                t('landing.unlimitedDrivers'),
                t('landing.perVehicleMonth').replace('{price}', formatPrice(currentPricing.professional)),
                t('landing.dailyCheckIns'),
                t('landing.maintenanceSchedulingFeature'),
                t('landing.aiDamageDetection'),
                t('landing.analyticsReportsFeature'),
                t('landing.csvExports'),
                t('landing.prioritySupport')
            ],
            highlighted: true,
            cta: t('landing.startFreeTrial'),
            badge: t('landing.mostPopular')
        },
        {
            name: t('landing.enterprise'),
            description: t('landing.enterpriseDesc'),
            vehicleLimit: 30,
            pricePerVehicle: currentPricing.enterprise,
            features: (selectedEdition === 'carrier' || selectedEdition === 'hybrid') ? [
                t('landing.upToVehicles').replace('{count}', '30'),
                t('landing.unlimitedDrivers'),
                t('landing.perVehicleMonth').replace('{price}', formatPrice(currentPricing.enterprise)),
                t('landing.allFeatures'),
                t('landing.aiDamageDetection'),
                language === 'el' ? 'AI Ανάλυση Φορτίου' : 'AI Cargo Analysis',
                language === 'el' ? 'Βελτιστοποίηση Στόλου' : 'Fleet Optimization',
                t('landing.analyticsReportsFeature'),
                t('landing.csvExports'),
                t('landing.phoneSupport'),
                t('landing.dedicatedManager')
            ] : [
                t('landing.upToVehicles').replace('{count}', '30'),
                t('landing.unlimitedDrivers'),
                t('landing.perVehicleMonth').replace('{price}', formatPrice(currentPricing.enterprise)),
                t('landing.aiDamageDetection'),
                t('landing.analyticsReportsFeature'),
                t('landing.csvExports'),
                t('landing.phoneSupport'),
                t('landing.dedicatedManager')
            ],
            highlighted: false,
            cta: t('landing.startFreeTrial')
        },
        {
            name: t('landing.custom'),
            description: t('landing.customDesc'),
            vehicleLimit: null,
            pricePerVehicle: null,
            features: [
                t('landing.unlimitedVehicles'),
                t('landing.unlimitedDrivers'),
                t('landing.allFeatures'),
                t('landing.customIntegrations'),
                t('landing.dedicatedManager'),
                t('landing.slaGuarantee'),
                t('landing.onPremise')
            ],
            highlighted: false,
            cta: t('landing.contactSales')
        }
    ];

    const plans = selectedEdition === 'leasing'
        ? basePlans.map((plan) => (plan.vehicleLimit ? { ...plan, features: leasingFeatures(String(plan.vehicleLimit), formatPrice(plan.pricePerVehicle)) } : plan))
        : basePlans;

    const features = [
        {
            icon: Truck,
            title: t('landing.vehicleManagement'),
            description: t('landing.vehicleManagementDesc')
        },
        {
            icon: Users,
            title: t('landing.driverManagement'),
            description: t('landing.driverManagementDesc')
        },
        {
            icon: Camera,
            title: t('landing.photoInspections'),
            description: t('landing.photoInspectionsDesc')
        },
        {
            icon: Wrench,
            title: t('landing.maintenanceScheduling'),
            description: t('landing.maintenanceSchedulingDesc')
        },
        {
            icon: BarChart3,
            title: t('landing.analyticsReports'),
            description: t('landing.analyticsReportsDesc')
        },
        {
            icon: Shield,
            title: t('landing.roleBasedAccess'),
            description: t('landing.roleBasedAccessDesc')
        }
    ];

    return (
        <div className="min-h-screen bg-white">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-slate-200 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        {/* Logo */}
                        <div className="flex items-center">
                            <img 
                                src="https://customer-assets-agu9un31.emergentagent.net/job_fleet-ops-62/artifacts/sxi4lya9_%CE%91%CE%BD%CF%89%CC%81%CE%BD%CF%85%CE%BC%CE%BF%20%CF%83%CF%87%CE%B5%CC%81%CE%B4%CE%B9%CE%BF.webp" 
                                alt="Cofleet Logo" 
                                className="object-contain"
                                style={{ height: '52px', width: 'auto' }}
                            />
                        </div>

                        {/* Desktop Navigation */}
                        <div className="hidden md:flex items-center gap-8">
                            <a href="#features" className="text-slate-600 hover:text-slate-800 transition-colors">{t('landing.features')}</a>
                            <a href="#pricing" className="text-slate-600 hover:text-slate-800 transition-colors">{t('landing.pricing')}</a>
                            <button 
                                onClick={() => navigate('/demo')}
                                className="text-slate-600 hover:text-slate-800 transition-colors"
                                data-testid="nav-demo-btn"
                            >
                                {language === 'en' ? 'Demo' : 'Demo'}
                            </button>
                            
                            {/* Language Toggle */}
                            <button
                                onClick={toggleLanguage}
                                className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 hover:border-[#1B7EB5] text-slate-600 hover:text-[#1B7EB5] transition-colors"
                                data-testid="landing-language-toggle"
                            >
                                <Globe className="w-4 h-4" />
                                <span className="text-sm font-medium">{language === 'en' ? 'EN' : 'ΕΛ'}</span>
                            </button>
                            
                            <Button 
                                variant="ghost" 
                                onClick={() => navigate('/login')}
                                className="text-slate-600"
                                data-testid="nav-signin-btn"
                            >
                                {t('landing.signIn')}
                            </Button>
                            <Button 
                                onClick={() => navigate('/register')}
                                className="bg-[#1B7EB5] hover:bg-[#1B7EB5]/90 text-white"
                                data-testid="nav-signup-btn"
                            >
                                {t('landing.startFreeTrial')}
                            </Button>
                        </div>

                        {/* Mobile Menu Button */}
                        <button 
                            className="md:hidden p-2"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        >
                            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>

                    {/* Mobile Menu */}
                    {mobileMenuOpen && (
                        <div className="md:hidden py-4 border-t border-slate-200">
                            <div className="flex flex-col gap-4">
                                <a href="#features" className="text-slate-600 hover:text-slate-800 px-2 py-1">{t('landing.features')}</a>
                                <a href="#pricing" className="text-slate-600 hover:text-slate-800 px-2 py-1">{t('landing.pricing')}</a>
                                <button 
                                    onClick={() => navigate('/demo')}
                                    className="text-slate-600 hover:text-slate-800 px-2 py-1 text-left"
                                >
                                    Demo
                                </button>
                                
                                {/* Mobile Language Toggle */}
                                <button
                                    onClick={toggleLanguage}
                                    className="flex items-center gap-2 px-2 py-1 text-slate-600"
                                    data-testid="mobile-language-toggle"
                                >
                                    <Globe className="w-4 h-4" />
                                    <span>{language === 'en' ? 'Ελληνικά' : 'English'}</span>
                                </button>
                                
                                <Button 
                                    variant="outline" 
                                    onClick={() => navigate('/login')}
                                    className="w-full"
                                >
                                    {t('landing.signIn')}
                                </Button>
                                <Button 
                                    onClick={() => navigate('/register')}
                                    className="w-full bg-[#1B7EB5] hover:bg-[#1B7EB5]/90 text-white"
                                >
                                    {t('landing.startFreeTrial')}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 via-white to-[#1B7EB5]/5">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center max-w-4xl mx-auto">
                        {/* Large Logo */}
                        <div className="flex justify-center mb-8">
                            <img 
                                src="https://customer-assets-agu9un31.emergentagent.net/job_fleet-ops-62/artifacts/sxi4lya9_%CE%91%CE%BD%CF%89%CC%81%CE%BD%CF%85%CE%BC%CE%BF%20%CF%83%CF%87%CE%B5%CC%81%CE%B4%CE%B9%CE%BF.webp" 
                                alt="Cofleet Logo" 
                                className="object-contain max-w-full"
                                style={{ height: '220px', width: 'auto' }}
                            />
                        </div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-800 leading-tight mb-6">
                            {t('landing.heroTitle')}
                            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#1B7EB5] to-[#8CC774]">
                                {t('landing.heroTitleHighlight')}
                            </span>
                        </h1>
                        <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
                            {t('landing.heroSubtitle')}
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <Button 
                                onClick={() => navigate('/register')}
                                className="w-full sm:w-auto bg-[#1B7EB5] hover:bg-[#1B7EB5]/90 text-white h-12 px-8 text-lg"
                                data-testid="hero-trial-btn"
                            >
                                {t('landing.startFreeTrial')}
                                <ChevronRight className="w-5 h-5 ml-2" />
                            </Button>
                            <Button 
                                variant="outline"
                                onClick={() => navigate('/login')}
                                className="w-full sm:w-auto h-12 px-8 text-lg border-slate-300"
                                data-testid="hero-signin-btn"
                            >
                                {t('landing.signIn')}
                            </Button>
                        </div>
                        <p className="text-sm text-slate-500 mt-4">{t('landing.noCreditCard')}</p>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
                            {t('landing.featuresTitle')}
                        </h2>
                        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                            {t('landing.featuresSubtitle')}
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {features.map((feature, index) => (
                            <div 
                                key={index}
                                className="p-6 rounded-xl border border-slate-200 hover:border-[#1B7EB5]/30 hover:shadow-lg transition-all duration-300"
                            >
                                <div className="w-12 h-12 bg-[#1B7EB5]/10 rounded-lg flex items-center justify-center mb-4">
                                    <feature.icon className="w-6 h-6 text-[#1B7EB5]" />
                                </div>
                                <h3 className="text-xl font-semibold text-slate-800 mb-2">{feature.title}</h3>
                                <p className="text-slate-600">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
                            {t('landing.pricingTitle')}
                        </h2>
                        <p className="text-lg text-slate-600 mb-8">
                            {t('landing.pricingSubtitle')}
                        </p>
                        
                        {/* Edition Toggle */}
                        <div className="flex flex-col items-center gap-4 mb-8">
                            <p className="text-sm text-slate-500">
                                {language === 'el' ? 'Επιλέξτε την έκδοση που ταιριάζει στις ανάγκες σας' : 'Choose the edition that fits your needs'}
                            </p>
                            <div className="inline-flex items-center bg-white rounded-xl p-1.5 shadow-lg border border-slate-200 flex-wrap justify-center gap-1" data-testid="edition-toggle">
                                <button
                                    onClick={() => setSelectedEdition('personal')}
                                    className={`flex items-center gap-2 px-4 sm:px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                                        selectedEdition === 'personal'
                                            ? 'bg-violet-600 text-white shadow-md'
                                            : 'text-slate-600 hover:bg-slate-100'
                                    }`}
                                    data-testid="edition-personal-btn"
                                >
                                    <User className="w-5 h-5" />
                                    <span className="hidden sm:inline">Personal</span>
                                    <span className="sm:hidden">Personal</span>
                                </button>
                                <button
                                    onClick={() => setSelectedEdition('inspection')}
                                    className={`flex items-center gap-2 px-4 sm:px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                                        selectedEdition === 'inspection'
                                            ? 'bg-[#1B7EB5] text-white shadow-md'
                                            : 'text-slate-600 hover:bg-slate-100'
                                    }`}
                                    data-testid="edition-inspection-btn"
                                >
                                    <Car className="w-5 h-5" />
                                    <span className="hidden sm:inline">Inspection</span>
                                    <span className="sm:hidden">Inspection</span>
                                </button>
                                <button
                                    onClick={() => setSelectedEdition('carrier')}
                                    className={`flex items-center gap-2 px-4 sm:px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                                        selectedEdition === 'carrier'
                                            ? 'bg-[#8CC774] text-white shadow-md'
                                            : 'text-slate-600 hover:bg-slate-100'
                                    }`}
                                    data-testid="edition-carrier-btn"
                                >
                                    <Package className="w-5 h-5" />
                                    <span className="hidden sm:inline">Carrier</span>
                                    <span className="sm:hidden">Carrier</span>
                                </button>
                                <button
                                    onClick={() => setSelectedEdition('hybrid')}
                                    className={`flex items-center gap-2 px-4 sm:px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                                        selectedEdition === 'hybrid'
                                            ? 'bg-orange-500 text-white shadow-md'
                                            : 'text-slate-600 hover:bg-slate-100'
                                    }`}
                                    data-testid="edition-hybrid-btn"
                                >
                                    <Layers className="w-5 h-5" />
                                    <span className="hidden sm:inline">Hybrid</span>
                                    <span className="sm:hidden">Hybrid</span>
                                </button>
                                <button
                                    onClick={() => setSelectedEdition('leasing')}
                                    className={`flex items-center gap-2 px-4 sm:px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                                        selectedEdition === 'leasing'
                                            ? 'bg-amber-500 text-white shadow-md'
                                            : 'text-slate-600 hover:bg-slate-100'
                                    }`}
                                    data-testid="edition-leasing-btn"
                                >
                                    <Handshake className="w-5 h-5" />
                                    <span>Leasing</span>
                                </button>
                                <button
                                    onClick={() => setSelectedEdition('workshop')}
                                    className={`flex items-center gap-2 px-4 sm:px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                                        selectedEdition === 'workshop'
                                            ? 'bg-teal-700 text-white shadow-md'
                                            : 'text-slate-600 hover:bg-slate-100'
                                    }`}
                                    data-testid="edition-workshop-btn"
                                >
                                    <Wrench className="w-5 h-5" />
                                    <span>{language === 'el' ? 'Συνεργείο' : 'Workshop'}</span>
                                </button>
                            </div>
                            
                            {/* Edition Description */}
                            <div className={`max-w-lg p-4 rounded-xl border-2 transition-all duration-300 ${
                                selectedEdition === 'personal'
                                    ? 'bg-violet-500/5 border-violet-500/20'
                                    : selectedEdition === 'inspection' 
                                        ? 'bg-[#1B7EB5]/5 border-[#1B7EB5]/20' 
                                        : selectedEdition === 'hybrid'
                                            ? 'bg-orange-500/5 border-orange-500/20'
                                            : selectedEdition === 'leasing'
                                                ? 'bg-amber-500/5 border-amber-500/20'
                                                : selectedEdition === 'workshop'
                                                    ? 'bg-teal-600/5 border-teal-600/20'
                                                    : 'bg-[#8CC774]/5 border-[#8CC774]/20'
                            }`}>
                                {selectedEdition === 'personal' ? (
                                    <div className="text-center">
                                        <div className="flex items-center justify-center gap-2 mb-2">
                                            <User className="w-5 h-5 text-violet-600" />
                                            <span className="font-semibold text-violet-600">Personal Edition</span>
                                        </div>
                                        <p className="text-sm text-slate-600">
                                            {language === 'el' 
                                                ? 'Ιδανικό για οικογένειες με έως 3 οχήματα & 3 οδηγούς. Mobile-first εφαρμογή με Χάρτη Στάθμευσης και Parking Safety.'
                                                : 'Perfect for families with up to 3 vehicles & 3 drivers. Mobile-first app with Parking Map and Parking Safety.'
                                            }
                                        </p>
                                    </div>
                                ) : selectedEdition === 'inspection' ? (
                                    <div className="text-center">
                                        <div className="flex items-center justify-center gap-2 mb-2">
                                            <Car className="w-5 h-5 text-[#1B7EB5]" />
                                            <span className="font-semibold text-[#1B7EB5]">Inspection Edition</span>
                                        </div>
                                        <p className="text-sm text-slate-600">
                                            {language === 'el' 
                                                ? 'Ιδανικό για επιβατικά οχήματα, ταξί, ενοικιαζόμενα & εταιρικούς στόλους. Εστιάζει στην παρακολούθηση, συντήρηση και επιθεώρηση οχημάτων.'
                                                : 'Perfect for passenger vehicles, taxis, rentals & corporate fleets. Focused on vehicle tracking, maintenance and inspections.'
                                            }
                                        </p>
                                    </div>
                                ) : selectedEdition === 'leasing' ? (
                                    <div className="text-center" data-testid="edition-leasing-description">
                                        <div className="flex items-center justify-center gap-2 mb-2">
                                            <Handshake className="w-5 h-5 text-amber-600" />
                                            <span className="font-semibold text-amber-600">Leasing Edition</span>
                                        </div>
                                        <p className="text-sm text-slate-600">
                                            {language === 'el'
                                                ? 'Για εταιρείες leasing. Οι μισθωτές σας συνδέονται αυτόματα, αναθέτετε οχήματα και παρακολουθείτε χιλιόμετρα, λήξεις συμβάσεων και όρια km.'
                                                : 'For leasing companies. Your lessees link automatically, you assign vehicles and track km, contract ends and km limits.'
                                            }
                                        </p>
                                    </div>
                                ) : selectedEdition === 'workshop' ? (
                                    <div className="text-center" data-testid="edition-workshop-description">
                                        <div className="flex items-center justify-center gap-2 mb-2">
                                            <Wrench className="w-5 h-5 text-teal-700" />
                                            <span className="font-semibold text-teal-700">{language === 'el' ? 'Έκδοση Συνεργείου' : 'Workshop Edition'}</span>
                                        </div>
                                        <p className="text-sm text-slate-600">
                                            {language === 'el'
                                                ? 'Για συνεργεία. Λαμβάνετε ραντεβού από εταιρείες leasing, οργανώνετε την εβδομάδα σας με βάση τη χωρητικότητα και εξυπηρετείτε και τους δικούς σας πελάτες.'
                                                : 'For garages. Receive appointments from leasing companies, plan your week around capacity and serve your own customers too.'
                                            }
                                        </p>
                                    </div>
                                ) : selectedEdition === 'hybrid' ? (
                                    <div className="text-center">
                                        <div className="flex items-center justify-center gap-2 mb-2">
                                            <Layers className="w-5 h-5 text-orange-500" />
                                            <span className="font-semibold text-orange-500">Hybrid Edition</span>
                                        </div>
                                        <p className="text-sm text-slate-600">
                                            {language === 'el' 
                                                ? 'Για εταιρείες με μικτό στόλο - επιβατικά & φορτηγά. Ιδανικό για οργανισμούς με πωλητές και παραδόσεις. Όλες οι δυνατότητες σε μία έκδοση.'
                                                : 'For companies with mixed fleets - passenger vehicles & trucks. Ideal for organizations with salespeople and deliveries. All features in one edition.'
                                            }
                                        </p>
                                    </div>
                                ) : (
                                    <div className="text-center">
                                        <div className="flex items-center justify-center gap-2 mb-2">
                                            <Package className="w-5 h-5 text-[#8CC774]" />
                                            <span className="font-semibold text-[#8CC774]">Carrier Edition</span>
                                        </div>
                                        <p className="text-sm text-slate-600">
                                            {language === 'el' 
                                                ? 'Πλήρης έκδοση για μεταφορικές εταιρείες. Περιλαμβάνει AI ανάλυση φορτίου, βελτιστοποίηση χωρητικότητας και όλες τις λειτουργίες.'
                                                : 'Complete edition for carriers & logistics. Includes AI cargo analysis, capacity optimization and all features.'
                                            }
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {selectedEdition === 'workshop' ? (
                        <WorkshopEditionCard el={language === 'el'} onSignup={() => navigate('/register', { state: { plan: 'basic', edition: 'workshop' } })} />
                    ) : (
                    <div className={`grid gap-6 ${selectedEdition === 'personal' ? 'md:grid-cols-1 max-w-md mx-auto' : 'md:grid-cols-2 lg:grid-cols-4'}`}>
                        {plans.map((plan, index) => (
                            <div 
                                key={index}
                                className={`relative bg-white rounded-2xl p-6 ${
                                    plan.highlighted 
                                        ? 'border-2 border-[#1B7EB5] shadow-xl scale-105' 
                                        : 'border border-slate-200 shadow-sm'
                                }`}
                                data-testid={`plan-${plan.name.toLowerCase()}`}
                            >
                                {plan.badge && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                        <span className="bg-[#1B7EB5] text-white text-xs font-semibold px-3 py-1 rounded-full">
                                            {plan.badge}
                                        </span>
                                    </div>
                                )}
                                <div className="text-center mb-6">
                                    <h3 className="text-xl font-bold text-slate-800 mb-2">{plan.name}</h3>
                                    <p className="text-sm text-slate-500 mb-4">{plan.description}</p>
                                    {plan.pricePerVehicle !== null ? (
                                        <div>
                                            <span className="text-4xl font-bold text-slate-800">
                                                €{typeof plan.pricePerVehicle === 'object' 
                                                    ? `${plan.pricePerVehicle.min}-${plan.pricePerVehicle.max}` 
                                                    : plan.pricePerVehicle}
                                            </span>
                                            <span className="text-slate-500">/{language === 'el' ? 'όχημα/μήνα' : 'vehicle/mo'}</span>
                                            {selectedEdition === 'hybrid' && typeof plan.pricePerVehicle === 'object' && (
                                                <p className="text-xs text-orange-500 mt-1">
                                                    {language === 'el' 
                                                        ? `€${plan.pricePerVehicle.min} Inspection / €${plan.pricePerVehicle.max} Carrier`
                                                        : `€${plan.pricePerVehicle.min} Inspection / €${plan.pricePerVehicle.max} Carrier`
                                                    }
                                                </p>
                                            )}
                                            <p className="text-sm text-[#8CC774] mt-2">
                                                {language === 'el' ? 'Έως' : 'Up to'} {plan.vehicleLimit} {language === 'el' ? 'οχήματα' : 'vehicles'}
                                            </p>
                                        </div>
                                    ) : (
                                        <div>
                                            <span className="text-2xl font-bold text-slate-800">{t('landing.custom')}</span>
                                            <p className="text-sm text-slate-500 mt-1">{t('landing.contactForPricing')}</p>
                                        </div>
                                    )}
                                </div>
                                <ul className="space-y-3 mb-6">
                                    {plan.features.map((feature, fIndex) => (
                                        <li key={fIndex} className="flex items-start gap-2">
                                            <Check className="w-5 h-5 text-[#8CC774] flex-shrink-0 mt-0.5" />
                                            <span className="text-sm text-slate-600">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                                <Button 
                                    onClick={() => navigate(plan.pricePerVehicle === null ? '/contact' : '/register', { 
                                        state: { plan: plan.name.toLowerCase(), vehicleLimit: plan.vehicleLimit, edition: selectedEdition } 
                                    })}
                                    className={`w-full ${
                                        plan.highlighted 
                                            ? 'bg-[#1B7EB5] hover:bg-[#1B7EB5]/90 text-white' 
                                            : 'bg-slate-100 hover:bg-slate-200 text-slate-800'
                                    }`}
                                    data-testid={`plan-${plan.name.toLowerCase()}-cta`}
                                >
                                    {plan.cta}
                                </Button>
                            </div>
                        ))}
                    </div>
                    )}
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#1B7EB5] to-[#58A6B4]">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                        {t('landing.ctaTitle')}
                    </h2>
                    <p className="text-lg text-white/90 mb-8">
                        {t('landing.ctaSubtitle')}
                    </p>
                    <Button 
                        onClick={() => navigate('/register')}
                        className="bg-white text-[#1B7EB5] hover:bg-slate-100 h-12 px-8 text-lg"
                        data-testid="cta-trial-btn"
                    >
                        {t('landing.startYourFreeTrial')}
                        <ChevronRight className="w-5 h-5 ml-2" />
                    </Button>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-800">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-3">
                            <img 
                                src="https://customer-assets-agu9un31.emergentagent.net/job_fleet-ops-62/artifacts/sxi4lya9_%CE%91%CE%BD%CF%89%CC%81%CE%BD%CF%85%CE%BC%CE%BF%20%CF%83%CF%87%CE%B5%CC%81%CE%B4%CE%B9%CE%BF.webp" 
                                alt="Cofleet Logo" 
                                className="h-8 w-auto object-contain"
                            />
                            <span className="text-white font-semibold">Cofleet</span>
                        </div>
                        <p className="text-slate-400 text-sm">
                            © {new Date().getFullYear()} Cofleet. {t('landing.allRightsReserved')}
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
